// Kas & Bank — E-Statement Tab (upload CSV + list per rekening + period filter).
// 2026-09-23 (user request).
import { useCallback, useEffect, useMemo, useState } from "react";
import { FileUp, FileDown, Trash2, Upload, X, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import {
  fileToBase64, previewEstatement, createEstatement, listEstatements,
  deleteEstatement, listEstatementRows, currentPeriodMonth, exportEstatement,
} from "@/lib/kasBankEstatement";

const MONTHS = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];

export default function EstatementTab({ canWrite, canDelete }) {
  const [accounts, setAccounts] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const period = `${year}-${month}`;
  const [allEsts, setAllEsts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadFor, setUploadFor] = useState(null); // account
  const [detail, setDetail] = useState(null); // est with rows

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([
        api.get("/company-bank-accounts").then((r) => r.data || []),
        listEstatements({}),
      ]);
      setAccounts(a);
      setAllEsts(e);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat data.");
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const ests = useMemo(() => allEsts.filter((x) => x.period_month === period), [allEsts, period]);

  // Reminder: saldo awal periode vs saldo akhir periode sebelumnya (per rekening).
  const mismatchByEst = useMemo(() => {
    const map = {};
    const byAcc = {};
    for (const e of allEsts) (byAcc[e.account_id] ||= []).push(e);
    for (const list of Object.values(byAcc)) {
      list.sort((a, b) => a.period_month.localeCompare(b.period_month));
      for (let i = 1; i < list.length; i++) {
        const prev = list[i - 1];
        const cur = list[i];
        if (prev.closing_balance != null && cur.opening_balance != null &&
            Math.abs(prev.closing_balance - cur.opening_balance) > 0.01) {
          map[cur.id] = { prev, cur };
        }
      }
    }
    return map;
  }, [allEsts]);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return [now + 1, now, now - 1, now - 2, now - 3];
  }, []);

  const openDetail = async (est) => {
    try {
      const rows = await listEstatementRows(est.id);
      setDetail({ est, rows });
    } catch (err) { toast.error("Gagal memuat baris."); }
  };
  const removeEst = async (est) => {
    if (!window.confirm(`Hapus e-statement ${est.filename} periode ${est.period_month}? Semua baris ikut terhapus.`)) return;
    try {
      await deleteEstatement(est.id);
      toast.success("E-statement dihapus.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghapus."); }
  };

  return (
    <div className="bg-white border border-slate-200" data-testid="est-tab">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <FileUp size={15} className="text-[#0B1D3A]" />
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">E-Statement Bank</div>
        <span className="text-[10px] text-slate-400 italic">Import CSV/XLS/XLSX per rekening &amp; periode · periode bisa parsial (mis. tgl 1–7, 8–15, 16–31) — rentang antar impor tidak boleh tumpang-tindih</span>
        <div className="ml-auto flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="est-month"
            className="border border-slate-300 px-2 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]">
            {MONTHS.map((m, i) => <option key={i} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="est-year"
            className="border border-slate-300 px-2 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-[11px] italic text-slate-400" data-testid="est-loading">Memuat…</div>
      ) : accounts.length === 0 ? (
        <div className="p-8 text-center text-[11px] text-slate-400">Belum ada rekening perusahaan.</div>
      ) : (
        <table className="w-full text-xs" data-testid="est-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Bank</th>
              <th className="text-left px-3 py-2">Nomor Rekening</th>
              <th className="text-left px-3 py-2">Status Periode {period}</th>
              <th className="text-right px-3 py-2">Jumlah Baris</th>
              <th className="text-right px-3 py-2">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => {
              const accEsts = ests.filter((x) => x.account_id === a.id);
              return (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`est-row-${a.id}`}>
                  <td className="px-3 py-2 font-semibold">{a.bank_name}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-500">{a.account_no}</td>
                  <td className="px-3 py-2">
                    {accEsts.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] uppercase tracking-wider font-semibold"><CheckCircle size={12} /> Sudah Import ({accEsts.length} rentang)</span>
                    ) : (
                      <span className="text-slate-400 text-[10px] italic">Belum ada</span>
                    )}
                    {accEsts.map((x) => (
                      <div key={x.id} className="text-[10px] text-slate-500 mt-0.5">
                        {x.filename} · <span className="tabular-nums" data-testid={`est-range-${x.id}`}>{(x.period_start || `${x.period_month}-01`)} s/d {(x.period_end || x.period_month)}</span>
                      </div>
                    ))}
                    {accEsts.map((x) => mismatchByEst[x.id] && (
                      <div key={`mm-${x.id}`} className="flex items-start gap-1 mt-1 text-[10px] text-amber-700" data-testid={`est-mismatch-${a.id}`}>
                        <AlertTriangle size={11} className="mt-px shrink-0" />
                        <span>Saldo awal {formatIDR(mismatchByEst[x.id].cur.opening_balance)} ≠ saldo akhir periode {mismatchByEst[x.id].prev.period_month} ({formatIDR(mismatchByEst[x.id].prev.closing_balance)})</span>
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{accEsts.length ? accEsts.reduce((s, x) => s + (x.row_count || 0), 0) : "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="space-y-1">
                      {accEsts.map((x) => (
                        <div key={x.id} className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => openDetail(x)} data-testid={`est-detail-${x.id}`}
                            className="px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold">Lihat Baris</button>
                          <button type="button" onClick={() => exportEstatement(x.id).catch((err) => toast.error(err?.response?.data?.detail || "Gagal export Excel."))}
                            title="Ekspor Excel format Buku Bank (semua baris, termasuk yang belum di-breakdown)"
                            data-testid={`est-export-${x.id}`}
                            className="px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-[10px] uppercase tracking-wider font-semibold">
                            <FileDown size={11} className="inline" /> Export
                          </button>
                          {canDelete && (
                            <button type="button" onClick={() => removeEst(x)} data-testid={`est-delete-${x.id}`}
                              className="px-2 py-1 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[10px] uppercase tracking-wider font-semibold">
                              <Trash2 size={11} className="inline" />
                            </button>
                          )}
                        </div>
                      ))}
                      {canWrite && (
                        <button type="button" onClick={() => setUploadFor(a)} data-testid={`est-upload-${a.id}`}
                          title="Import e-statement — bisa potongan rentang tanggal (mis. 1–7, 8–15, 16–31)"
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold">
                          <Upload size={11} /> Import
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {uploadFor && (
        <UploadDialog account={uploadFor} period={period} onClose={() => setUploadFor(null)} onSaved={() => { setUploadFor(null); load(); }} />
      )}
      {detail && (
        <DetailDialog data={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

function UploadDialog({ account, period, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [delimiter, setDelimiter] = useState(",");
  const [preview, setPreview] = useState(null); // {headers, sample, row_count, suggested_mapping}
  const [mapping, setMapping] = useState({ date: "", description: "", debit: "", credit: "", balance: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // 2026-09-29 (user request): periode e-statement bisa parsial — user memilih
  // rentang tanggal file (default full bulan). Backend menolak rentang yang
  // tumpang-tindih pada rekening yang sama.
  const [yy, mm] = period.split("-").map(Number);
  const monthFirst = `${period}-01`;
  const monthLast = `${period}-${String(new Date(yy, mm, 0).getDate()).padStart(2, "0")}`;
  const [dateFrom, setDateFrom] = useState(monthFirst);
  const [dateTo, setDateTo] = useState(monthLast);

  const doPreview = async () => {
    if (!file) { toast.error("Pilih file e-statement (CSV/XLS/XLSX) dulu."); return; }
    try {
      setLoading(true);
      const b64 = await fileToBase64(file);
      const p = await previewEstatement(b64, delimiter);
      setPreview(p);
      setMapping({
        date: p.suggested_mapping.date || "",
        description: p.suggested_mapping.description || "",
        debit: p.suggested_mapping.debit || "",
        credit: p.suggested_mapping.credit || "",
        amount: p.suggested_mapping.amount || "",
        balance: p.suggested_mapping.balance || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal preview file e-statement.");
    } finally { setLoading(false); }
  };

  const doSave = async () => {
    if (!file || !preview) return;
    if (!mapping.date || !mapping.description || (!mapping.debit && !mapping.credit && !mapping.amount)) {
      toast.error("Isi mapping kolom minimal: Tanggal, Deskripsi, dan Debit/Kredit atau Nominal.");
      return;
    }
    if (!dateFrom || !dateTo) { toast.error("Isi rentang tanggal periode file."); return; }
    if (dateFrom > dateTo) { toast.error("'Dari Tanggal' tidak boleh melebihi 'Sampai Tanggal'."); return; }
    try {
      setSaving(true);
      const b64 = await fileToBase64(file);
      const res = await createEstatement({
        account_id: account.id, period_month: period, period_start: dateFrom, period_end: dateTo, filename: file.name,
        delimiter, file_b64: b64, mapping, date_format: "auto",
      });
      toast.success(`Import berhasil: ${res.row_count} baris (${res.matched_count} auto-match).`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal import.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="est-upload-dialog">
      <div className="bg-white w-full max-w-3xl border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="overline text-[10px] text-slate-500 tracking-widest">IMPORT E-STATEMENT</div>
            <div className="font-display text-base font-semibold text-[#0B1D3A]">{account.bank_name} · {account.account_no} · {period}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-[11px]">
          <div className="grid grid-cols-3 gap-2 items-end">
            <div className="col-span-2">
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">File E-Statement * <span className="normal-case text-[9px] text-slate-400">(CSV, XLS, XLSX)</span></div>
              <input type="file" accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="est-file"
                className="w-full border border-slate-300 px-2 py-1 text-[11px] bg-white" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Delimiter <span className="normal-case text-[9px] text-slate-400">(CSV saja)</span></div>
              <select value={delimiter} onChange={(e) => setDelimiter(e.target.value)} data-testid="est-delimiter"
                className="w-full border border-slate-300 px-2 py-1 text-[11px] bg-white">
                <option value=",">, (koma)</option>
                <option value=";">; (titik-koma)</option>
                <option value="\t">Tab</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Dari Tanggal *</div>
              <input type="date" value={dateFrom} min={monthFirst} max={monthLast} onChange={(e) => setDateFrom(e.target.value)} data-testid="est-date-from"
                className="w-full border border-slate-300 px-2 py-1 text-[11px] bg-white" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Sampai Tanggal *</div>
              <input type="date" value={dateTo} min={monthFirst} max={monthLast} onChange={(e) => setDateTo(e.target.value)} data-testid="est-date-to"
                className="w-full border border-slate-300 px-2 py-1 text-[11px] bg-white" />
            </div>
          </div>
          <p className="text-[9px] text-slate-400 italic -mt-1" data-testid="est-period-hint">File bisa berisi potongan periode (mis. tgl 1–7, 8–15, 16–31). Rentang tanggal antar impor pada rekening yang sama tidak boleh tumpang-tindih.</p>
          <button type="button" onClick={doPreview} disabled={!file || loading} data-testid="est-preview-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-800 disabled:opacity-40 text-white text-[10px] uppercase tracking-wider font-semibold">
            {loading ? "Membaca…" : "Preview & Deteksi Kolom"}
          </button>

          {preview && (
            <>
              <div className="border border-slate-200 p-2 bg-slate-50">
                <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Mapping Kolom * (dari header {preview.row_count} baris)</div>
                <div className="grid grid-cols-2 gap-2">
                  {[["date","Tanggal"],["description","Deskripsi"],["debit","Debit (keluar)"],["credit","Kredit (masuk)"],["amount","Nominal (jika debit/kredit gabung)"],["balance","Saldo (opsional — utk kolom SALDO export)"]].map(([k, lbl]) => (
                    <div key={k}>
                      <div className="text-[10px] text-slate-600 mb-0.5">{lbl}</div>
                      <select value={mapping[k] || ""} onChange={(e) => setMapping({ ...mapping, [k]: e.target.value })} data-testid={`est-map-${k}`}
                        className="w-full border border-slate-300 px-2 py-1 text-[11px] bg-white">
                        <option value="">— tidak ada —</option>
                        {preview.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto max-h-56 border border-slate-200">
                <table className="w-full text-[10px]">
                  <thead className="bg-slate-100 sticky top-0"><tr>{preview.headers.map((h) => <th key={h} className="text-left px-2 py-1 border-b border-slate-200">{h}</th>)}</tr></thead>
                  <tbody>
                    {preview.sample.map((row, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        {preview.headers.map((_, j) => <td key={j} className="px-2 py-1">{row[j] ?? ""}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50/70">
          <button type="button" onClick={onClose} className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold">Batal</button>
          <button type="button" onClick={doSave} disabled={!preview || saving} data-testid="est-save"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] disabled:opacity-40 text-white text-[10px] uppercase tracking-wider font-semibold">
            <Upload size={12} /> {saving ? "Menyimpan…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailDialog({ data, onClose }) {
  const { est, rows } = data;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="est-detail-dialog">
      <div className="bg-white w-full max-w-5xl max-h-[80vh] overflow-hidden border border-slate-200 shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="overline text-[10px] text-slate-500 tracking-widest">DETAIL E-STATEMENT</div>
            <div className="font-display text-base font-semibold text-[#0B1D3A]">{est.account_bank_name} · {est.period_month} · {est.filename}</div>
            <div className="text-[10px] text-slate-500">{est.row_count} baris · {est.error_count > 0 ? `${est.error_count} error di-skip` : "tanpa error"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Tanggal</th>
                <th className="text-left px-3 py-2">Deskripsi</th>
                <th className="text-right px-3 py-2">Debit</th>
                <th className="text-right px-3 py-2">Kredit</th>
                <th className="text-left px-3 py-2">Auto-Match</th>
                <th className="text-left px-3 py-2">Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-1.5">{r.description}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">{r.debit > 0 ? formatIDR(r.debit) : ""}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{r.credit > 0 ? formatIDR(r.credit) : ""}</td>
                  <td className="px-3 py-1.5">
                    {r.matched_movement_id ? (
                      <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" title={r.matched_description}>Sudah tercatat</span>
                    ) : (
                      <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-slate-100 text-slate-600 border border-slate-200">Belum</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    {r.breakdown_kind ? (
                      <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-[#0B1D3A] text-white">{r.breakdown_kind}</span>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">— buka tab Breakdown —</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
